import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Container,
    Row,
    Col,
    Card,
    CardHeader,
    CardBody,
    Table,
    Button,
    FormGroup,
    Label,
    Input,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Spinner,
    FormText
} from 'reactstrap';
import { toast } from 'react-toastify';
import { apiURL1 } from '../../config';
import Header from '../../components/Headers/Header';
import { FaEdit, FaTrash, FaPlus } from 'react-icons/fa';
import Footer from 'components/Footers/Footer';

const baseURL = apiURL1;

function GradeSystem() {
    const token = localStorage.getItem("token");

    const [grades, setGrades] = useState([]);
    const [modal, setModal] = useState(false);
    const [editModal, setEditModal] = useState(false);
    const [deleteModal, setDeleteModal] = useState(false);
    const [gradeToDelete, setGradeToDelete] = useState(null);
    const [selectedGrade, setSelectedGrade] = useState(null);
    const [startPercentage, setStartPercentage] = useState('');
    const [endPercentage, setEndPercentage] = useState('');
    const [grade, setGrade] = useState('');
    const [loading, setLoading] = useState(true);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        fetchGrades();
    }, []);

    const fetchGrades = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${baseURL}/superadmin/grades`, {
                headers: {
                    'Authorization': `Bearer ${token}`, // Include the token in the headers
                }
            });
            if (response.data && response.data.success && Array.isArray(response.data.data)) {
                setGrades(response.data.data);
            } else {
                setGrades([]); // Ensure grades is always an array
                if (response.data && !response.data.success) {
                    toast.error(response.data.message || 'Failed to fetch grades');
                }
            }
        } catch (error) {
            console.error('Error fetching grades:', error);
            toast.error('Error fetching grades');
            setGrades([]); // Ensure grades is always an array
        } finally {
            setLoading(false);
        }
    };

    const toggleModal = () => {
        setModal(!modal);
        setStartPercentage('');
        setEndPercentage('');
        setGrade('');
        setErrors({});
    };

    const toggleEditModal = (gradeData) => {
        setEditModal(!editModal);
        setSelectedGrade(gradeData);
        setStartPercentage(gradeData.startPercentage);
        setEndPercentage(gradeData.endPercentage);
        setGrade(gradeData.grade);
        setErrors({});
    };

    const openDeleteModal = (gradeId) => {
        setGradeToDelete(gradeId);
        setDeleteModal(true);
    };

    const closeDeleteModal = () => {
        setGradeToDelete(null);
        setDeleteModal(false);
    };

    const validate = () => {
        let isValid = true;
        const newErrors = {};

        if (!startPercentage) {
            newErrors.startPercentage = 'Start percentage is required';
            isValid = false;
        } else if (isNaN(startPercentage)) {
            newErrors.startPercentage = 'Start percentage must be a number';
            isValid = false;
        }

        if (!endPercentage) {
            newErrors.endPercentage = 'End percentage is required';
            isValid = false;
        } else if (isNaN(endPercentage)) {
            newErrors.endPercentage = 'End percentage must be a number';
            isValid = false;
        }

        if (parseFloat(startPercentage) >= parseFloat(endPercentage)) {
            newErrors.startPercentage = 'Start percentage must be less than end percentage';
            newErrors.endPercentage = 'End percentage must be greater than start percentage';
            isValid = false;
        }

        if (!grade) {
            newErrors.grade = 'Grade is required';
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    };

    const handleAddGrade = async () => {
        if (validate()) {
            setLoading(true);
            try {
                const newGrade = { startPercentage, endPercentage, grade };
                const response = await axios.post(`${baseURL}/superadmin/grades`, newGrade, {
                    headers: {
                        'Authorization': `Bearer ${token}`, // Include the token in the headers
                    }
                });
                if (response.data.success) {
                    toast.success('Grade added successfully!');
                    fetchGrades();
                    toggleModal();
                } else {
                    toast.error(response.data.message || 'Error adding grade');
                }
            } catch (error) {
                console.error('Error adding grade:', error);
                toast.error(error.response?.data?.message || 'Error adding grade');
            } finally {
                setLoading(false);
            }
        }
    };

    const handleUpdateGrade = async () => {
        if (validate()) {
            setLoading(true);
            try {
                const updatedGrade = { startPercentage, endPercentage, grade };
                const response = await axios.put(`${baseURL}/superadmin/grades/${selectedGrade._id}`, updatedGrade, {
                    headers: {
                        'Authorization': `Bearer ${token}`, // Include the token in the headers
                    }
                });
                if (response.data.success) {
                    toast.success('Grade updated successfully!');
                    fetchGrades();
                    toggleEditModal({});
                } else {
                    toast.error(response.data.message || 'Error updating grade');
                }
            } catch (error) {
                console.error('Error updating grade:', error);
                toast.error(error.response?.data?.message || 'Error updating grade');
            } finally {
                setLoading(false);
            }
        }
    };

    const handleDeleteGrade = async () => {
        setLoading(true);
        try {
            await axios.delete(`${baseURL}/superadmin/grades/${gradeToDelete}`, {
                headers: {
                    'Authorization': `Bearer ${token}`, // Include the token in the headers
                }
            });
            toast.success('Grade deleted successfully!');
            fetchGrades();
        } catch (error) {
            console.error('Error deleting grade:', error);
            toast.error(error.response?.data?.message || 'Error deleting grade');
        } finally {
            setLoading(false);
            closeDeleteModal();
        }
    };

    if (loading) {
        return (
            <>
                <Header />
                <Container>
                    <Row>
                        <Col>
                            <div style={{ display: 'flex', justifyContent: 'center' }}>
                                <Spinner color="primary" />
                            </div>
                        </Col>
                    </Row>
                </Container>
            </>
        );
    }

    return (
        <>
            <Header />
            <Container>
                <Row>
                    <Col>
                        <Card>
                            <CardHeader style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <h3>Grade System</h3>
                                <Button color="primary" size="sm" onClick={toggleModal}>
                                    <FaPlus /> Add New
                                </Button>
                            </CardHeader>
                            <CardBody>
                                <Table className="table">
                                    <thead>
                                        <tr style={{ backgroundColor: '#2980b9', color: 'white' }}>
                                            <th>Start Percentage</th>
                                            <th>End Percentage</th>
                                            <th>Grade</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {grades.map(gradeData => (
                                            <tr key={gradeData._id}>
                                                <td>{gradeData.startPercentage}</td>
                                                <td>{gradeData.endPercentage}</td>
                                                <td>{gradeData.grade}</td>
                                                <td>
                                                    <Button color="warning" size="sm" onClick={() => toggleEditModal(gradeData)} style={{ marginRight: '5px' }}>
                                                        <FaEdit /> Edit
                                                    </Button>
                                                    <Button color="danger" size="sm" onClick={() => openDeleteModal(gradeData._id)}>
                                                        <FaTrash /> Delete
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                {/* Add Grade Modal */}
                <Modal isOpen={modal} toggle={toggleModal}>
                    <ModalHeader toggle={toggleModal}>Add New Grade</ModalHeader>
                    <ModalBody>
                        <FormGroup>
                            <Label for="startPercentage">Start Percentage</Label>
                            <Input
                                type="number"
                                name="startPercentage"
                                id="startPercentage"
                                placeholder="Enter start percentage"
                                value={startPercentage}
                                onChange={(e) => setStartPercentage(e.target.value)}
                                invalid={!!errors.startPercentage}
                            />
                            {errors.startPercentage && <FormText color="danger">{errors.startPercentage}</FormText>}
                        </FormGroup>
                        <FormGroup>
                            <Label for="endPercentage">End Percentage</Label>
                            <Input
                                type="number"
                                name="endPercentage"
                                id="endPercentage"
                                placeholder="Enter end percentage"
                                value={endPercentage}
                                onChange={(e) => setEndPercentage(e.target.value)}
                                invalid={!!errors.endPercentage}
                            />
                            {errors.endPercentage && <FormText color="danger">{errors.endPercentage}</FormText>}
                        </FormGroup>
                        <FormGroup>
                            <Label for="grade">Grade</Label>
                            <Input
                                type="text"
                                name="grade"
                                id="grade"
                                placeholder="Enter grade"
                                value={grade}
                                onChange={(e) => setGrade(e.target.value)}
                                invalid={!!errors.grade}
                            />
                            {errors.grade && <FormText color="danger">{errors.grade}</FormText>}
                        </FormGroup>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="primary" onClick={handleAddGrade} disabled={loading}>
                            {loading ? <Spinner size="sm" /> : 'Add'}
                        </Button>
                        <Button color="secondary" onClick={toggleModal}>
                            Cancel
                        </Button>
                    </ModalFooter>
                </Modal>

                {/* Edit Grade Modal */}
                <Modal isOpen={editModal} toggle={() => toggleEditModal({})}>
                    <ModalHeader toggle={() => toggleEditModal({})}>Edit Grade</ModalHeader>
                    <ModalBody>
                        <FormGroup>
                            <Label for="startPercentage">Start Percentage</Label>
                            <Input
                                type="number"
                                name="startPercentage"
                                id="startPercentage"
                                placeholder="Enter start percentage"
                                value={startPercentage}
                                onChange={(e) => setStartPercentage(e.target.value)}
                                invalid={!!errors.startPercentage}
                            />
                            {errors.startPercentage && <FormText color="danger">{errors.startPercentage}</FormText>}
                        </FormGroup>
                        <FormGroup>
                            <Label for="endPercentage">End Percentage</Label>
                            <Input
                                type="number"
                                name="endPercentage"
                                id="endPercentage"
                                placeholder="Enter end percentage"
                                value={endPercentage}
                                onChange={(e) => setEndPercentage(e.target.value)}
                                invalid={!!errors.endPercentage}
                            />
                            {errors.endPercentage && <FormText color="danger">{errors.endPercentage}</FormText>}
                        </FormGroup>
                        <FormGroup>
                            <Label for="grade">Grade</Label>
                            <Input
                                type="text"
                                name="grade"
                                id="grade"
                                placeholder="Enter grade"
                                value={grade}
                                onChange={(e) => setGrade(e.target.value)}
                                invalid={!!errors.grade}
                            />
                            {errors.grade && <FormText color="danger">{errors.grade}</FormText>}
                        </FormGroup>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="primary" onClick={handleUpdateGrade} disabled={loading}>
                            {loading ? <Spinner size="sm" /> : 'Update'}
                        </Button>
                        <Button color="secondary" onClick={() => toggleEditModal({})}>
                            Cancel
                        </Button>
                    </ModalFooter>
                </Modal>

                {/* Delete Grade Modal */}
                <Modal isOpen={deleteModal}>
                    <ModalHeader toggle={closeDeleteModal}>
                        Delete Grade

                    </ModalHeader>
                    <ModalBody>
                        <p>Are you sure you want to delete this grade?</p>
                        <Button color="danger" onClick={handleDeleteGrade}>
                            Yes, Delete
                        </Button>
                        <Button color="secondary" onClick={closeDeleteModal}>
                            Cancel
                        </Button>
                    </ModalBody>
                </Modal>
                 <Footer />
            </Container>
        </>
    );
}

export default GradeSystem;
